import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { genreOptions, regionOptions, yearOptions } from "@/mocks/categories";
import usePageMeta from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel from "@/pages/category/components/FilterPanel";

const ratingOptions = [
  { label: "全部评分", value: "all" },
  { label: "8.5 分以上", value: "high" },
  { label: "8.0 - 8.5 分", value: "mid" },
  { label: "8.0 分以下", value: "low" },
];

const sortOptions = [
  { label: "最近上映", value: "year" },
  { label: "评分最高", value: "rating" },
  { label: "最受欢迎", value: "popularity" },
];

const PAGE_SIZE = 8;

export default function Category() {
  const [searchParams] = useSearchParams();
  const initialGenre = searchParams.get("genre") || "全部";

  usePageMeta({
    title: "爱情电影分类 - 爱的色放电影",
    description:
      "浏览爱的色放电影提供的丰富爱情电影资源，支持多维筛选与排序，快速找到你想看的爱情佳作。",
    keywords: "爱情电影分类,爱情电影大全,电影筛选,电影推荐,爱的色放电影",
  });

  const [genre, setGenre] = useState(initialGenre);
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState("year");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const resetAll = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setRating("all");
    setSort("year");
    setPage(1);
  };

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      const okGenre = genre === "全部" || m.genres.includes(genre) || m.tags.includes(genre);
      const okRegion = region === "全部" || m.region === region;
      const okYear = year === "全部" || m.year === Number(year);
      let okRating = true;
      if (rating === "high") okRating = m.rating >= 8.5;
      else if (rating === "mid") okRating = m.rating >= 8.0 && m.rating < 8.5;
      else if (rating === "low") okRating = m.rating < 8.0;
      return okGenre && okRegion && okYear && okRating;
    });

    return list.sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "popularity") return b.popularity - a.popularity;
      return b.year - a.year;
    });
  }, [genre, region, year, rating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilter = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-10 pt-28 md:pt-32 pb-12">
      <div className="max-w-[1440px] mx-auto">
        {/* breadcrumb */}
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">电影分类</span>
        </nav>

        <div className="mb-6">
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            爱情电影大全
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            按类型、地区、年份与评分多维筛选，找到此刻最想看的爱情故事
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* sidebar */}
          <aside className="w-full lg:w-72 shrink-0">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer mb-3"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选条件
              </span>
              <i className={`${filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`}></i>
            </button>
            <div className={`${filterOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-28`}>
              <FilterPanel
                genreOptions={genreOptions}
                regionOptions={regionOptions}
                yearOptions={yearOptions}
                ratingOptions={ratingOptions}
                genre={genre}
                region={region}
                year={year}
                rating={rating}
                onGenre={updateFilter(setGenre)}
                onRegion={updateFilter(setRegion)}
                onYear={updateFilter(setYear)}
                onRating={updateFilter(setRating)}
                onReset={resetAll}
              />
            </div>
          </aside>

          {/* main */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <p className="text-sm text-foreground-600">
                共找到 <span className="text-primary-600 font-semibold">{filtered.length}</span> 部电影
              </p>
              <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                {sortOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSort(s.value)}
                    className={`px-3 py-1.5 rounded-full text-xs cursor-pointer transition-colors whitespace-nowrap ${
                      sort === s.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {paged.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center rounded-lg bg-background-50 border border-background-200">
                <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
                <h3 className="mt-4 font-heading font-semibold text-lg text-foreground-900">
                  未找到符合筛选条件的爱情电影
                </h3>
                <p className="mt-2 text-sm text-foreground-500">
                  换个筛选条件，或者重置后再试试看
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {paged.map((m) => (
                    <MovieCard key={m.id} movie={m} showCast />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setPage(currentPage - 1)}
                      className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        className={`w-9 h-9 rounded-md text-sm cursor-pointer transition-colors ${
                          p === currentPage
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage(currentPage + 1)}
                      className="w-9 h-9 rounded-md border border-background-300 flex items-center justify-center text-foreground-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}